/**
 * Voice-of-X feature hooks — TanStack Query wrappers around the
 * ``@ando/core/api`` endpoint helpers.
 *
 * Lives separately from the ingest-side ``features/report/hooks.ts``
 * (which is a static catalog of imported POC reports) because:
 *   - generation is server-state, not bundled JSON,
 *   - cache keys need to coexist (the catalog uses no key at all),
 *   - mutations + invalidation are first-class here.
 *
 * Pattern source (DRY): ``features/projects/hooks/useProjects.ts``
 * (lines 60-75) — same ``staleTime`` / key shape / invalidate-on-mutate
 * rhythm. Auto-refetch while a run is non-terminal mirrors
 * ``features/connectors/hooks/useCapabilityConnections.ts`` (lines 54-77).
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  type CreateVoiceReportInput,
  type ListVoiceReportsParams,
  type RunVoiceReportInput,
  type RunVoiceReportResponse,
  type UpdateVoiceReportInput,
  type VoiceReport,
  type VoiceReportRun,
  type VoiceReportRunsListResponse,
  type VoiceReportsListResponse,
  cancelVoiceReportRun,
  createVoiceReport,
  deleteVoiceReport,
  getVoiceReport,
  listVoiceReportRuns,
  listVoiceReports,
  runVoiceReport,
  updateVoiceReport,
} from "@ando/core/api";

// ---------------------------------------------------------------------------
// Canonical query keys — exported so mutations elsewhere can invalidate
// without re-writing the key shape (DRY pattern from ``useProjects``).
// ---------------------------------------------------------------------------

export const voiceReportsListKey = (params?: ListVoiceReportsParams) =>
  ["voice-reports", { limit: params?.limit ?? 50, offset: params?.offset ?? 0 }] as const;

export const voiceReportKey = (reportId: string) =>
  ["voice-reports", reportId] as const;

export const voiceReportRunsKey = (
  reportId: string,
  params?: ListVoiceReportsParams,
) =>
  [
    "voice-reports",
    reportId,
    "runs",
    { limit: params?.limit ?? 50, offset: params?.offset ?? 0 },
  ] as const;

const REPORTS_STALE_MS = 60_000;
const REPORT_STALE_MS = 60_000;
const RUNS_STALE_MS = 30_000;

/** Auto-refetch cadence while any run in the page is non-terminal. */
const POLL_WHILE_TRANSIENT_MS = 4_000;
const TERMINAL_RUN_STATUSES = new Set<VoiceReportRun["status"]>([
  "success",
  "failed",
  "cancelled",
]);

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Shared list query — hook + `/workspace/reports` route loader (intent preload). */
export const voiceReportsListQueryOptions = (params?: ListVoiceReportsParams) =>
  queryOptions({
    queryKey: voiceReportsListKey(params),
    queryFn: () => listVoiceReports(params),
    staleTime: REPORTS_STALE_MS,
  });

/** GET ``/voice-reports`` — paginated list. */
export function useVoiceReports(
  params?: ListVoiceReportsParams,
): UseQueryResult<VoiceReportsListResponse> {
  return useQuery(voiceReportsListQueryOptions(params));
}

/** GET ``/voice-reports/{id}`` — single report definition. */
export function useVoiceReport(
  reportId: string | null | undefined,
): UseQueryResult<VoiceReport> {
  return useQuery<VoiceReport>({
    queryKey: voiceReportKey(reportId ?? ""),
    queryFn: () => getVoiceReport(reportId as string),
    enabled: Boolean(reportId),
    staleTime: REPORT_STALE_MS,
  });
}

/**
 * GET ``/voice-reports/{id}/runs`` — history.
 *
 * Polls while any row is non-terminal so the Runs tab pulse-refreshes
 * without the user having to click. Steady-state cost is zero (no live
 * runs → ``refetchInterval`` returns ``false``).
 */
export function useVoiceReportRuns(
  reportId: string | null | undefined,
  params?: ListVoiceReportsParams,
): UseQueryResult<VoiceReportRunsListResponse> {
  return useQuery<VoiceReportRunsListResponse>({
    queryKey: voiceReportRunsKey(reportId ?? "", params),
    queryFn: () => listVoiceReportRuns(reportId as string, params),
    enabled: Boolean(reportId),
    staleTime: RUNS_STALE_MS,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const hasTransient = data.runs.some(
        (r) => !TERMINAL_RUN_STATUSES.has(r.status),
      );
      return hasTransient ? POLL_WHILE_TRANSIENT_MS : false;
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** POST ``/voice-reports`` — create a definition. Invalidates the list. */
export function useCreateVoiceReport(): UseMutationResult<
  VoiceReport,
  Error,
  CreateVoiceReportInput
> {
  const qc = useQueryClient();
  return useMutation<VoiceReport, Error, CreateVoiceReportInput>({
    mutationFn: (input) => createVoiceReport(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["voice-reports"] });
    },
  });
}

export interface UpdateVoiceReportVars {
  reportId: string;
  patch: UpdateVoiceReportInput;
}

/**
 * PATCH ``/voice-reports/{id}`` — partial update.
 *
 * Pessimistic by default (consistent with ``projects`` PATCH flows): we
 * wait for the BE row before invalidating, which keeps the cache and the
 * DB in lockstep without a roll-back layer.
 */
export function useUpdateVoiceReport(): UseMutationResult<
  VoiceReport,
  Error,
  UpdateVoiceReportVars
> {
  const qc = useQueryClient();
  return useMutation<VoiceReport, Error, UpdateVoiceReportVars>({
    mutationFn: ({ reportId, patch }) => updateVoiceReport(reportId, patch),
    onSuccess: (next) => {
      qc.setQueryData(voiceReportKey(next.id), next);
      void qc.invalidateQueries({ queryKey: ["voice-reports"] });
    },
  });
}

/** DELETE ``/voice-reports/{id}`` — hard delete + cache prune. */
export function useDeleteVoiceReport(): UseMutationResult<
  void,
  Error,
  { reportId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { reportId: string }>({
    mutationFn: ({ reportId }) => deleteVoiceReport(reportId),
    onSuccess: (_v, vars) => {
      qc.removeQueries({ queryKey: voiceReportKey(vars.reportId) });
      void qc.invalidateQueries({ queryKey: ["voice-reports"] });
    },
  });
}

export interface RunVoiceReportVars extends RunVoiceReportInput {
  reportId: string;
}

/** POST ``/voice-reports/{id}/run`` — dispatch Temporal workflow. */
export function useRunVoiceReport(): UseMutationResult<
  RunVoiceReportResponse,
  Error,
  RunVoiceReportVars
> {
  const qc = useQueryClient();
  return useMutation<RunVoiceReportResponse, Error, RunVoiceReportVars>({
    mutationFn: ({ reportId, dataset_id }) =>
      runVoiceReport(reportId, { dataset_id }),
    onSuccess: (_data, vars) => {
      // Pull the runs list immediately so the new ``pending`` row appears
      // without the staleTime delay.
      void qc.invalidateQueries({
        queryKey: ["voice-reports", vars.reportId, "runs"],
      });
    },
  });
}

/** POST ``/voice-reports/runs/{id}/cancel`` — cancel pending/running. */
export function useCancelVoiceReportRun(): UseMutationResult<
  VoiceReportRun,
  Error,
  { runId: string; reportId: string }
> {
  const qc = useQueryClient();
  return useMutation<VoiceReportRun, Error, { runId: string; reportId: string }>({
    mutationFn: ({ runId }) => cancelVoiceReportRun(runId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ["voice-reports", vars.reportId, "runs"],
      });
    },
  });
}
